import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {MilestonePickerMilestoneItem} from '@github-ui/item-picker-milestone'
import {useCallback} from 'react'

import {useUpdateIssueMilestoneBulkMutation} from './mutations/use-update-issue-milestone-bulk-mutation'
import type {UpdateIssueMilestoneBulkRequest} from './api/api-update-issue-milestone-bulk'
import {MilestonePicker, type MilestonePickerProps} from './MilestonePicker'

export type BulkIssueMilestonePickerProps = Omit<
  MilestonePickerProps,
  'issueId' | 'onIssueUpdate' | 'onSelectionChanged'
> &
  SharedBulkActionsItemPickerProps & {onMutate: (input: UpdateIssueMilestoneBulkRequest) => void}

export function BulkIssueMilestonePicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  ...rest
}: BulkIssueMilestonePickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueMilestoneBulkMutation({onMutate})
  const onSelectionChange = useCallback(
    (selectedMilestone: MilestonePickerMilestoneItem[]) => {
      const milestoneId =
        selectedMilestone.length === 1 && selectedMilestone[0]?.id !== 'no-milestone' ? selectedMilestone[0]?.id : null
      const clearMilestone = selectedMilestone[0]?.id === 'no-milestone'
      const ids = [...issuesToActOn]
      let input: UpdateIssueMilestoneBulkRequest | undefined = undefined
      if (milestoneId) {
        input = {ids, milestoneId}
      } else if (clearMilestone) {
        input = {ids, clearMilestone}
      }
      if (!input) return

      bulkUpdateMutate(input, {
        onSuccess: response => onCompleted?.(response.data.updateIssuesBulk.jobId),
        onError,
      })
    },
    [bulkUpdateMutate, issuesToActOn, onCompleted, onError],
  )

  return <MilestonePicker onSelectionChanged={onSelectionChange} {...rest} />
}

try{ BulkIssueMilestonePicker.displayName ||= 'BulkIssueMilestonePicker' } catch {}